/**
 * NewSelectTextQuiz Component
 * 새 UI - 단일 선택형 문제 with radio buttons (inline 형태)
 * - 정오답 표시 없음, 답안 제출만
 * - problem 텍스트 렌더링 없이 radio 버튼만 표시
 * - #태그 스타일 선택 UI
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { cn } from '../../lib/utils'
import { KatexText, SubmitButton, QuizMedia } from './shared'
import type { SelectTextQuizData, QuizSolveResult } from '../../types/legacyQuiz'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { parseSelectTextOptionModels } from './hooks'

interface NewSelectTextQuizProps {
  quizId: string | number
  quiz: SelectTextQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  submitLabel?: string
  hideSubmitButton?: boolean
}

export function SelectTextQuizForDiary({
  quizId,
  quiz,
  onSolve,
  solveResult,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
}: NewSelectTextQuizProps) {
  const { t } = useTranslation('quiz')
  const [startTime] = useState(() => Date.now())

  const isSolved = solveResult !== null && solveResult !== undefined

  // 선택 옵션들 파싱 - {{option1|option2|option3}} 형태에서 추출
  // 첫 번째 그룹의 옵션들만 사용 (단일 선택이므로)
  const options = useMemo(() => {
    const reg = /\{\{(.*?)}}/im
    const match = reg.exec(quiz.problem)
    if (!match) return []

    return parseSelectTextOptionModels(match[1]).filter(option => option.isSelectable)
  }, [quiz.problem])

  // 초기 answer 계산
  const computedInitialAnswer = initialAnswersProp?.[0] ?? ''

  const [selectedRawIndex, setSelectedRawIndex] = useState<number | null>(
    computedInitialAnswer !== '' ? parseInt(computedInitialAnswer) : null
  )
  const isInitialMount = useRef(true)

  useEffect(() => {
    if (computedInitialAnswer !== '') {
      setSelectedRawIndex(parseInt(computedInitialAnswer))
    } else {
      setSelectedRawIndex(null)
    }
  }, [computedInitialAnswer])

  // hideSubmitButton 모드에서 선택 변경 시 자동으로 onSolve 호출 (페이지 이동 시 상태 유지용)
  // Skip initial mount — parent already has the data from previousResponses.
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    if (hideSubmitButton && selectedRawIndex !== null && !isSolved && !isViewSolved) {
      onSolve?.(quizId, [selectedRawIndex.toString()], quiz.vars, Date.now() - startTime)
    }
  }, [selectedRawIndex, hideSubmitButton, isSolved, isViewSolved, onSolve, quizId, quiz.vars, startTime])

  const canSubmit = selectedRawIndex !== null

  // 선택 처리
  const handleSelect = useCallback((rawIndex: number) => {
    if (isSolved || isViewSolved) return
    setSelectedRawIndex(rawIndex)
  }, [isSolved, isViewSolved])

  // 제출 버튼 클릭
  const handleSubmit = useCallback(() => {
    if (!canSubmit || isSolved || selectedRawIndex === null) return
    const elapsedTime = Date.now() - startTime
    onSolve?.(quizId, [selectedRawIndex.toString()], quiz.vars, elapsedTime)
  }, [selectedRawIndex, canSubmit, isSolved, onSolve, quizId, quiz.vars, startTime])

  return (
    <div className="space-y-6">
      {/* 문제 제목 */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-3"
      >
        <div className="inline-flex gap-2 items-center px-3 py-1.5 text-sm font-bold text-white bg-linear-to-r from-blue-500 to-cyan-500 rounded-full shadow-2xs">
          <span>{t('types.newSelectText.label')}</span>
        </div>
        <h3 className="text-xl font-bold leading-relaxed text-gray-900">
          <KatexText text={quiz.title} />
        </h3>
        {/* 문제 이미지/영상/Desmos */}
        <QuizMedia image={quiz.image} imageMobile={quiz.imageMobile} desmos={quiz.desmos} vars={quiz.vars} />
      </motion.div>

      {/* #태그 스타일 Radio 버튼 그룹 */}
      <div
        role="radiogroup"
        aria-label={t('types.newSelectText.selectOption')}
        className="flex flex-wrap gap-2"
      >
        {options.map((option, visibleIndex) => {
          const isSelected = selectedRawIndex === option.rawIndex

          return (
            <motion.button
              key={`option-${option.rawIndex}`}
              role="radio"
              aria-checked={isSelected}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: visibleIndex * 0.03 }}
              onClick={() => handleSelect(option.rawIndex)}
              disabled={isSolved || isViewSolved}
              className={cn(
                'px-3 py-1.5 rounded-lg transition-all text-base font-medium',
                // 기본 상태
                !isSelected && 'bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-700',
                // 선택 상태
                isSelected && 'bg-blue-500 text-white shadow-2xs',
                // 제출 후 비활성화
                (isSolved || isViewSolved) && 'cursor-not-allowed opacity-70'
              )}
            >
              <span className="font-bold">#</span>
              <KatexText text={option.label} />
            </motion.button>
          )
        })}
      </div>

      {/* 제출 버튼 */}
      {!isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          label={submitLabel ?? t('types.newSelectText.submit')}
        />
      )}

      {/* 제출 완료 표시 */}
      {isSolved && (
        <div className="p-4 text-center text-gray-600 bg-gray-100 rounded-xl">
          {t('types.newSelectText.submitted')}
        </div>
      )}
    </div>
  )
}
